import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { useBlogComments, useCreateComment, useDeleteComment } from '@/hooks/useBlog';
import { Comment } from './Comment';
import { CommentForm } from './CommentForm';

interface CommentSectionProps {
  postId: string;
}

export function CommentSection({ postId }: CommentSectionProps) {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuthStore();
  const { data: comments, isLoading } = useBlogComments(postId);
  const createComment = useCreateComment(postId);
  const deleteComment = useDeleteComment(postId);

  const handleSubmit = (content: string) => {
    createComment.mutate({ content });
  };

  const handleReply = (content: string, parentId: string) => {
    createComment.mutate({ content, parentId });
  };

  const handleDelete = (commentId: string) => {
    deleteComment.mutate(commentId);
  };

  return (
    <div className="space-y-6">
      <h3 className="flex items-center gap-2 text-lg font-semibold">
        <MessageCircle className="h-5 w-5 text-emerald-500" />
        {t('blogPage.comments')}
        {comments && comments.length > 0 && (
          <span className="text-sm font-normal text-muted-foreground">
            ({comments.length})
          </span>
        )}
      </h3>

      {isAuthenticated ? (
        <CommentForm onSubmit={handleSubmit} isSubmitting={createComment.isPending} />
      ) : (
        <div className="rounded-lg border bg-muted/50 p-4 text-center">
          <p className="text-sm text-muted-foreground">
            {t('blogPage.loginToComment')}{' '}
            <Link to="/login" className="text-emerald-600 hover:underline font-medium">
              {t('common.signIn')}
            </Link>
          </p>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse flex gap-3">
              <div className="h-8 w-8 rounded-full bg-muted" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-24 rounded bg-muted" />
                <div className="h-3 w-full rounded bg-muted" />
              </div>
            </div>
          ))}
        </div>
      ) : comments && comments.length > 0 ? (
        <div className="divide-y">
          {comments.map((comment) => (
            <Comment
              key={comment.id}
              comment={comment}
              currentUserId={user?.id}
              onReply={handleReply}
              onDelete={handleDelete}
              isSubmitting={createComment.isPending}
            />
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground text-center py-6">
          {t('blogPage.noComments')}
        </p>
      )}
    </div>
  );
}
